/**
 * Fetch a binary resource while reporting byte-level progress via the Streams API.
 *
 * Why not rely on Three.js's loader.onProgress? Two reasons:
 *   1. It fires per-file (0 → 100 with nothing in between for single big assets).
 *   2. With HTTP transfer-encoding chunked / gzip, browsers often skip onProgress entirely.
 *
 * This helper falls back gracefully:
 *   - No Content-Length header → onProgress(loaded, 0) on each chunk; caller treats total=0
 *     as "indeterminate" and shows a stripe instead of a percentage.
 *   - No ReadableStream support (very old browsers) → single onProgress(total, total) at end.
 */
export async function fetchWithProgress(
  url: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Fetch ${url} failed: HTTP ${res.status}`);
  }
  const totalHeader = res.headers.get('Content-Length');
  const total = totalHeader ? parseInt(totalHeader, 10) : 0;

  if (!res.body || typeof res.body.getReader !== 'function') {
    // No stream support — fall back to the full buffer.
    const buf = await res.arrayBuffer();
    onProgress(buf.byteLength, buf.byteLength);
    return buf;
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress(loaded, total);
  }

  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out.buffer;
}
